import React, { useState, useEffect } from "react";
import AdminSidebar from "../../component/AdminComp/AdminSidebar";
import AdminTopbar from "../../component/AdminComp/AdminTopbar";
import { Download } from "react-feather";
import { Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import axios from "axios";
import { baseurl, imageUrl } from "../../Baseurl";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const getBadgeClass = (status) => {
  if (!status || status.trim() === "") return "bg-primary";
  switch (status.toLowerCase()) {
    case "processing":
      return "bg-warning";
    case "out for delivery":
      return "bg-info";
    case "delivered":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const AdminSellerAllOrder = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderIdSearch, setOrderIdSearch] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const { id } = useParams();

  useEffect(() => {
    if (!id) {
      Swal.fire("Error", "Seller ID is missing!", "error");
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${baseurl}getOrdersBySellerId/${id}`);
        const data = response.data.orders || [];
        setOrders(data);
        setFilteredOrders(data);
      } catch (error) {
        console.error("Error fetching orders:", error);
        Swal.fire("Error", "Failed to load orders", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [id]);

  useEffect(() => {
    let filtered = [...orders];

    if (search) {
      filtered = filtered.filter(order =>
        order.matched_cart_items.some(item =>
          item.title.toLowerCase().includes(search.toLowerCase())
        )
      );
    }

    if (orderIdSearch) {
      filtered = filtered.filter(order =>
        order.order_unique_id.toLowerCase().includes(orderIdSearch.toLowerCase())
      );
    }

    if (paymentFilter !== "All") {
      filtered = filtered.filter(order => order.payment_method === paymentFilter);
    }

    if (startDate && endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset to first page on filter
  }, [search, orderIdSearch, paymentFilter, startDate, endDate, orders]);

  const handleDownloadExcel = () => {
    const dataToExport = [];
    orders.forEach(order => {
      order.matched_cart_items.forEach(item => {
        dataToExport.push({
          "Order ID": order.order_unique_id,
          "Order Total (₹)": parseFloat(order.total).toFixed(2),
          "Payment Method": order.payment_method,
          "Order Date": new Date(order.created_at).toLocaleString(),
          "Item Title": item.title,
          "Quantity": item.quantity,
          "Price (₹)": item.price,
          "Delivery Charge (₹)": item.delivery_charge,
          "Delivery Method": item.delivery_method,
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Seller Order Items");
    XLSX.writeFile(wb, "seller_order_items.xlsx");
  };

  const paymentMethods = [...new Set(orders.map(order => order.payment_method))];

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredOrders.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredOrders.length);

  return (
    <div className="App" style={{ display: "flex" }}>
      <AdminSidebar />
      <div className="content" style={{ flexGrow: 1, padding: "20px" }}>
        <AdminTopbar />
        <div className="container-fluid">
          <div className="mb-2">
            <h4 className="text-start">Seller's All Orders</h4>
            <div className="row g-2 mb-3">
              <div className="col-md-2">
                <input type="text" className="form-control" placeholder="Search by Product"
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="col-md-2">
                <input type="text" className="form-control" placeholder="Search by Order ID"
                  onChange={(e) => setOrderIdSearch(e.target.value)} />
              </div>
              <div className="col-md-2">
                <DatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="Start Date"
                  className="form-control"
                />
              </div>
              <div className="col-md-2">
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="End Date"
                  className="form-control"
                />
              </div>
              <div className="col-md-2">
                <select
                  className="form-control"
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <option value="All">All Payments</option>
                  {paymentMethods.map((method, idx) => (
                    <option key={idx} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <span className="btn btnsuccess w-100" onClick={handleDownloadExcel}>
                  <Download size={17} /> Download
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : currentRecords.length === 0 ? (
                <p className="text-center">No orders found.</p>
              ) : (
                <div>
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Order ID & Status</th>
                        <th>Total (₹)</th>
                        <th>Payment</th>
                        <th>Date</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Price (₹)</th>
                        <th>Delivery</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((order, index) =>
                        order.matched_cart_items.map((item, i) => (
                          <tr key={`${order.order_id}-${item.cart_id}`}>
                            <td>{indexOfFirstRecord + index + 1}</td>
                            <td>
                              {order.order_unique_id}
                              <div>
                                <span className={`badge ${getBadgeClass(item.status_by_seller)} mt-1`}>
                                  {item.status_by_seller?.trim() || "Active"}
                                </span>
                              </div>
                            </td>
                            <td>₹{parseFloat(order.total).toFixed(2)}</td>
                            <td>{order.payment_method}</td>
                            <td>{new Date(order.created_at).toLocaleString()}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={`${imageUrl}listing/${item.image}`}
                                  alt={item.title}
                                  width="50"
                                  height="50"
                                  className="me-2 rounded"
                                />
                                <span>{item.title}</span>
                              </div>
                            </td>
                            <td>{item.quantity}</td>
                            <td>₹{item.price}</td>
                            <td>
                              ₹{item.delivery_charge} ({item.delivery_method})
                            </td>
                            <td>
                              <Link
                                to={`/admin/order-detail/${order.order_unique_id}/${id}`}
                                className="btn btn-sm btn-primary"
                              >
                                View
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between align-items-center p-3">
                    <div>
                      <label className="me-2">Records per page:</label>
                      <select
                        className="form-control d-inline-block"
                        style={{ width: "auto" }}
                        value={recordsPerPage}
                        onChange={(e) => {
                          setRecordsPerPage(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <div>
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                          </li>
                          {Array.from({ length: Math.ceil(filteredOrders.length / recordsPerPage) }, (_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === Math.ceil(filteredOrders.length / recordsPerPage) ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSellerAllOrder;
